import {Component, computed, effect, inject, signal, ChangeDetectionStrategy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {catchError, finalize, from, of} from 'rxjs';
import {rxResource} from '@angular/core/rxjs-interop';
import {Api} from '../../../../api/api';
import {getEnergySummaries} from '../../../../api/functions';
import {LocationStateService} from '../../../../common/services/location-state.service';
import {DayDatepickerComponent} from '../datepicker/day-datepicker.component';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {EnergyBalanceResponse} from '../../../../api/models/energy-balance-response';
import {buildAllDebugStatusSections, DebugTableSection} from '../energy-chart-debug/debug-status-table.helper';

interface DailyDebugBalanceRow {
  timestamp?: string;
  energyTotalIn?: number | null;
  energyTotalOut?: number | null;
  energyTotalInOut?: number | null;
}

interface DailyDebugBatteryToGridRow {
  read?: string;
  battery_to_grid_kw?: number | null;
}

interface DailyDebugBalanceNode {
  nodeId?: string;
  nodeName?: string;
  rows?: DailyDebugBalanceRow[];
  matchedDailyBalanceFields?: {[key: string]: number};
}

interface DailyDebugBalanceResponse {
  nodes?: DailyDebugBalanceNode[];
  separateNodes?: DailyDebugBalanceNode[];
  dailyBalanceResult?: EnergyBalanceResponse;
  battery_to_grid_derived?: DailyDebugBatteryToGridRow[];
}

interface DebugBalanceField {
  key: string;
  value: number | null | undefined;
}

type EnergyFieldKey = 'energyTotalIn' | 'energyTotalOut' | 'energyTotalInOut';

interface DebugBalanceFieldSummary {
  difference: number | null;
  increases: number;
  drops: number;
  monotone: boolean | null;
}

interface DebugBalanceTableSummary {
  energyTotalIn: DebugBalanceFieldSummary;
  energyTotalOut: DebugBalanceFieldSummary;
  energyTotalInOut: DebugBalanceFieldSummary;
}

interface DebugBalanceTable {
  id: string;
  name: string;
  rows: DailyDebugBalanceRow[];
  summary: DebugBalanceTableSummary;
  matchedFields: DebugBalanceField[];
}

const HIDDEN_MATCHED_FIELD_KEYS = new Set(['derived battery_to_grid', 'derived_battery_to_grid']);

@Component({
  selector: 'app-energy-balance-debug',
  imports: [CommonModule, RouterLink, DayDatepickerComponent],
  templateUrl: './energy-balance-debug.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../energy-chart/energy-chart.component.css',
})
export class EnergyBalanceDebugComponent {
  private readonly api = inject(Api);
  private readonly locationStateService = inject(LocationStateService);

  /** Timezone of the currently selected location; `undefined` uses the runtime default. */
  private get locationTimeZone(): string | undefined {
    return this.locationStateService.currentLocation()?.timezone;
  }
  private get locationDateTimeFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat('de-AT', {
      timeZone: this.locationTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  isLoading = signal<boolean>(false);
  isDebugStatusLoading = signal<boolean>(false);
  selectedDate = signal<Date>(new Date());
  locations = computed(() => this.locationStateService.locations());

  params = computed(() => ({
    location_id: this.locationStateService.currentLocation()?.id ?? this.locations()[0]?.id ?? '',
    aggregation: 'day',
    at: this.selectedDate().toISOString(),
  }));

  resource = rxResource({
    params: this.params,
    defaultValue: {nodes: []} as DailyDebugBalanceResponse,
    stream: (loaderParams) => {
      this.isLoading.set(true);
      return of({nodes: []} as DailyDebugBalanceResponse).pipe(
        finalize(() => this.isLoading.set(false)),
      );
    },
  });

  debugStatusResource = rxResource({
    params: this.params,
    defaultValue: undefined,
    stream: (loaderParams) => {
      this.isDebugStatusLoading.set(true);
      return from(this.api.invoke(getEnergySummaries, loaderParams.params)).pipe(
        catchError((error) => {
          console.error('Daily debug status service is not reachable.', error);
          return of({} as AverageEnergyResponse);
        }),
        finalize(() => this.isDebugStatusLoading.set(false)),
      );
    },
  });

  tables = computed<DebugBalanceTable[]>(() => {
    const response = this.resource.value();
    const nodes = [...(response?.nodes ?? []), ...(response?.separateNodes ?? [])];
    return nodes.map((node, index) => {
      const sortedRows = this.sortRows(node.rows ?? []);
      return {
        id: node.nodeId ?? `node-${index + 1}`,
        name: node.nodeName ?? node.nodeId ?? `Node ${index + 1}`,
        rows: sortedRows,
        summary: this.calculateTableSummary(sortedRows),
        matchedFields: this.toFieldEntries(node.matchedDailyBalanceFields),
      };
    });
  });
  debugStatusSections = computed<DebugTableSection[]>(() => buildAllDebugStatusSections(this.debugStatusResource.value()));

  dailyBalanceResult = computed<EnergyBalanceResponse | null>(() => this.resource.value()?.dailyBalanceResult ?? null);
  dailyBalanceResultPayload = computed<{dailyBalanceResult: EnergyBalanceResponse} | null>(() => {
    const result = this.dailyBalanceResult();
    return result ? {dailyBalanceResult: result} : null;
  });
  derivedBatteryToGridRows = computed<DailyDebugBatteryToGridRow[]>(() =>
    this.sortBatteryToGridRows(this.resource.value()?.battery_to_grid_derived ?? []),
  );

  updateDate(newDate: Date): void {
    if (!newDate) {
      return;
    }
    this.selectedDate.set(newDate);
  }

  formatValue(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '';
    }
    return value.toFixed(3);
  }

  formatTrendSummary(summary: DebugBalanceFieldSummary): string {
    if (summary.monotone === null) {
      return '';
    }
    return `up:${summary.increases} down:${summary.drops} mono:${summary.monotone ? 'Y' : 'N'}`;
  }

  formatTimestamp(timestamp: string | null | undefined): string {
    if (!timestamp) {
      return '';
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }

    return this.locationDateTimeFormatter.format(date);
  }

  private sortRows(rows: DailyDebugBalanceRow[]): DailyDebugBalanceRow[] {
    return [...rows].sort((a, b) => this.toTime(a.timestamp) - this.toTime(b.timestamp));
  }

  private sortBatteryToGridRows(rows: DailyDebugBatteryToGridRow[]): DailyDebugBatteryToGridRow[] {
    return [...rows].sort((a, b) => this.toTime(a.read) - this.toTime(b.read));
  }

  private calculateTableSummary(rows: DailyDebugBalanceRow[]): DebugBalanceTableSummary {
    return {
      energyTotalIn: this.calculateFieldSummary(rows, 'energyTotalIn'),
      energyTotalOut: this.calculateFieldSummary(rows, 'energyTotalOut'),
      energyTotalInOut: this.calculateFieldSummary(rows, 'energyTotalInOut'),
    };
  }

  private calculateFieldSummary(rows: DailyDebugBalanceRow[], key: EnergyFieldKey): DebugBalanceFieldSummary {
    const values = rows
      .map((row) => row[key])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    if (values.length === 0) {
      return {
        difference: null,
        increases: 0,
        drops: 0,
        monotone: null,
      };
    }

    let increases = 0;
    let drops = 0;

    for (let i = 1; i < values.length; i++) {
      const delta = values[i] - values[i - 1];
      if (delta > 0) {
        increases++;
      } else if (delta < 0) {
        drops++;
      }
    }

    return {
      difference: values[values.length - 1] - values[0],
      increases,
      drops,
      monotone: increases === 0 || drops === 0,
    };
  }

  private toFieldEntries(fieldMap: {[key: string]: number} | undefined): DebugBalanceField[] {
    if (!fieldMap) {
      return [];
    }

    return Object.entries(fieldMap)
      .map(([key, value]) => ({key, value}))
      .filter((field) => this.shouldShowMatchedField(field.key))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  private shouldShowMatchedField(key: string): boolean {
    return !HIDDEN_MATCHED_FIELD_KEYS.has(key.trim().toLowerCase());
  }

  private toTime(timestamp: string | undefined): number {
    if (!timestamp) {
      return Number.MAX_SAFE_INTEGER;
    }

    const time = new Date(timestamp).getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
  }

  constructor() {
    effect(() => {
      const availableLocations = this.locations();
      const currentLocation = this.locationStateService.currentLocation();
      if (!currentLocation && availableLocations.length > 0) {
        this.locationStateService.setLocation(availableLocations[0]);
      }
    });
  }
}
